import { Pressable, View, Image, Text, Linking, Platform, Button, useWindowDimensions, ViewStyle, Alert } from "react-native"
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from "@stylesheet"
// import { Text } from "@constants/Themed";
import { Feather, FontAwesome } from '@expo/vector-icons';
import { AVATAR } from '@assets/--native/AvatarBase64';
import React, { useEffect, useState, useMemo } from 'react';
import parseByDelimiter from "@utils/parseByDelimiter"
import HorizontalCarousel from "@components/Global/Carousel/HorizontalCarousel.native"
import MaskedView from '@react-native-masked-view/masked-view';
import { LinearGradient } from "expo-linear-gradient"
import { isTablet } from "react-native-device-info"
import reorder from './utils/reOrder';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import apis from "@api/api";
import handleImage from "@utils/handleImage";
import { set } from "lodash";
import moment from "moment";
import { H2, H5, Separator, SizableText, XStack } from "tamagui";
import { useDeviceType } from "@hooks/useDeviceType";
import RatingComponent from "@components/RatingComponent";
import getCurrencySymbol from "@hooks/getCurrency";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

export type DefaultDocumentProps = {
  item: any;
  dataId: string;
  navigation: any;
  maxWidth?: number;
  innerStyle?: ViewStyle
}

const DefaultDocument = ({ innerStyle, item, dataId, maxWidth, navigation }: DefaultDocumentProps) => {
  const { data } = item;
  const [initialArray, setInitialArray] = useState([]);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const queryClient = useQueryClient();
  const theme = themeSelector();
  const [key, setKey] = useState(0);

  const { isTablet } = useDeviceType();

  const tablet = isTablet;
  const pdfUrl = useMemo(() => {
    const directLink = item?.pdfLink;
    const nestedLink = item?.data?.pdfLink || item?.data?.signedPdfUrl;
    return directLink || nestedLink || null;
  }, [item?.pdfLink, item?.data]);
  // Extract person fields from item.data
  useEffect(() => {
    setInitialArray(getPersonFields(data));
  }, [item]);

  const getPersonFields = (obj) => {
    if (!obj) {
      return [];
    }
    const objLowercase = Object.keys(obj).reduce((acc, key) => {
      if (typeof obj[key] !== 'object') {
        acc[key.toLowerCase()] = obj[key];
      }
      return acc;
    }, {});

    // Person-specific priority properties for documents
    const priorityProperties = [
      "name", "fullname", "full name", "firstname", "first name",
      "lastname", "last name", "surname", "email", "phone", "telephone",
      "position", "title", "department", "company", "organization",
      "address", "city", "country", "signature", "date signed", "completed date"
    ];
    let result = [];
    const excludeProperties = ["_id", "image", "id", "created", "updated"];

    for (const property of priorityProperties) {
      if (objLowercase.hasOwnProperty(property) && !excludeProperties.includes(property)) {
        result.push([property, objLowercase[property]]);
      }
    }

    // Add any remaining fields that weren't captured
    for (const [key, value] of Object.entries(objLowercase)) {
      if (!result.find(([prop]) => prop === key) && !excludeProperties.includes(key)) {
        result.push([key, value]);
        if (result.length >= 8) {
          break;
        }
      }
    }

    return result.slice(0, 8); // Show up to 8 fields for documents
  };

  const DocumentContent = () => {
    const parseIfDate = (value) => {
      if (value) {
        if (value.includes('T') && moment(value, moment.ISO_8601, true).isValid()) {
          return moment(value).format('DD/MM/YYYY');
        }
      }
      return value;
    }

    // Get document title/name
    const titleData = initialArray.find(([key, value]) =>
      key.includes('name') || key.includes('title') || key.includes('document')
    );
    const documentTitle = titleData ? titleData[1] : 'Document';

    // Smart person name detection
    const getPersonName = () => {
      if (!initialArray || initialArray.length === 0) return 'Unknown Person';

      // Priority order for person name fields
      const nameFieldPriority = [
        'fullname', 'full name', 'complete name', 'full_name',
        'name', 'person name', 'person_name', 'personname',
        'firstname lastname', 'first_name last_name',
        'title', 'person title', 'person_title',
        'display name', 'display_name', 'displayname',
        'username', 'user name', 'user_name',
        'contact name', 'contact_name', 'contactname',
        'client name', 'client_name', 'clientname',
        'customer name', 'customer_name', 'customername',
        'applicant name', 'applicant_name', 'applicantname',
        'signatory', 'signer', 'signed by', 'signed_by',
        'firstname', 'first name', 'first_name',
        'lastname', 'last name', 'last_name', 'surname',
        'givenname', 'given name', 'given_name',
        'familyname', 'family name', 'family_name'
      ];

      // First, try to find exact matches
      for (const priority of nameFieldPriority) {
        const found = initialArray.find(([key, value]) =>
          key.toLowerCase() === priority && value && value.toString().trim() !== ''
        );
        if (found && found[1]) {
          return found[1];
        }
      }

      // Second, try partial matches
      for (const priority of nameFieldPriority) {
        const found = initialArray.find(([key, value]) =>
          key.toLowerCase().includes(priority) && value && value.toString().trim() !== ''
        );
        if (found && found[1]) {
          return found[1];
        }
      }

      // Third, try semantic matching for common name patterns
      const semanticNamePatterns = [
        /^(mr|mrs|ms|dr|prof|professor)\.?\s+/i, // Titles followed by names
        /\b(name|person|individual|client|customer|applicant)\b/i,
        /^[a-zA-Z]+\s+[a-zA-Z]+$/, // Two words (likely first+last name)
        /^[a-zA-Z]{2,}\s+[a-zA-Z]{2,}/, // Multiple words with 2+ chars each
      ];

      for (const [key, value] of initialArray) {
        if (value && typeof value === 'string') {
          const trimmedValue = value.toString().trim();
          // Check if the value looks like a person's name
          for (const pattern of semanticNamePatterns) {
            if (pattern.test(trimmedValue) && trimmedValue.length > 2 && trimmedValue.length < 100) {
              // Additional validation: not email, not URL, not just numbers
              if (!trimmedValue.includes('@') &&
                !trimmedValue.includes('http') &&
                !trimmedValue.includes('.com') &&
                !/^\d+$/.test(trimmedValue)) {
                return trimmedValue;
              }
            }
          }
        }
      }

      // Fourth, try to construct name from first+last name fields
      const firstName = initialArray.find(([key]) =>
        key.toLowerCase().includes('first') || key.toLowerCase().includes('given')
      );
      const lastName = initialArray.find(([key]) =>
        key.toLowerCase().includes('last') || key.toLowerCase().includes('surname') || key.toLowerCase().includes('family')
      );

      if (firstName && lastName && firstName[1] && lastName[1]) {
        return `${firstName[1]} ${lastName[1]}`;
      }

      // Last resort: use any field that might contain a name
      const possibleName = initialArray.find(([key, value]) => {
        const keyLower = key.toLowerCase();
        return value && value.toString().trim() !== '' &&
          (keyLower.includes('name') || keyLower.includes('person') ||
            keyLower.includes('user') || keyLower.includes('contact')) &&
          !keyLower.includes('company') && !keyLower.includes('business') &&
          !keyLower.includes('organization') && !keyLower.includes('file') &&
          !keyLower.includes('document');
      });

      if (possibleName && possibleName[1]) {
        return possibleName[1];
      }

      const signerEmail = item?.signedBy?.[0]?.email || item?.data?.email;
      if (signerEmail) {
        return signerEmail;
      }

      return 'Unknown Person';
    };

    // Get person name using smart detection
    const personName = getPersonName();

    // Get completion/signature date
    const dateData = initialArray.find(([key, value]) =>
      key.includes('date') || key.includes('signed') || key.includes('completed')
    );
    const completionDate = dateData ? parseIfDate(dateData[1]) : null;

    // Get all person details (excluding title, name, and date fields)
    const personDetails = initialArray.filter(([key, value]) =>
      !key.includes('image') &&
      !key.includes('document') &&
      !key.includes('title') &&
      !key.includes('date') &&
      !key.includes('signed') &&
      !key.includes('completed') &&
      !(key.includes('name') && !key.includes('company') && !key.includes('organization')) &&
      !['image', 'id'].includes(key)
    );

    return (
      <View style={[styles.modernColumn, { flex: 1 }]}>
        {/* Document Header */}
        <View style={styles.modernColumn}>
          <XStack justifyContent="space-between" alignItems="flex-start">
            <View style={{ flex: 1 }}>
              <SizableText
                userSelect={"none"}
                numberOfLines={2}
                style={[
                  styles.modernCardTitle,
                  {
                    fontSize: Platform.select({ web: 20, default: 18 }),
                    color: getModernTextColor(theme, 'primary'),
                    textAlign: "left",
                    lineHeight: Platform.select({ web: 24, default: 22 }),
                    fontWeight: "700",
                    marginBottom: 4,
                  }
                ]}
              >
                📄 {documentTitle}
              </SizableText>

              <SizableText
                style={{
                  fontSize: Platform.select({ web: 16, default: 14 }),
                  color: getModernTextColor(theme, 'secondary'),
                  fontWeight: "600",
                  marginBottom: 8,
                }}
              >
                Completed by: {personName}
              </SizableText>
            </View>

            {completionDate && (
              <View style={[
                getModernActionStyle(theme, 'blue'),
                {
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 12,
                  width: 'auto',
                  height: 'auto',
                }
              ]}>
                <SizableText
                  style={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: 12,
                  }}
                >
                  {completionDate}
                </SizableText>
              </View>
            )}
          </XStack>

          {item?.pdfLink && (
            <Pressable
              onPress={() => Linking.openURL(item.pdfLink)}
              style={{
                marginTop: 12,
                alignSelf: 'flex-start',
                paddingHorizontal: 14,
                paddingVertical: 8,
                borderRadius: 10,
                backgroundColor: theme ? '#3b82f6' : '#1d4ed8'
              }}
            >
              <SizableText
                style={{
                  color: '#ffffff',
                  fontWeight: '600',
                  fontSize: 12
                }}
              >
                View Signed PDF
              </SizableText>
            </Pressable>
          )}

          {/* Status Badge */}
          <XStack gap={10} alignItems="center" style={{ marginTop: 8 }}>
            <View style={[
              getModernActionStyle(theme, 'green'),
              {
                paddingHorizontal: 8,
                paddingVertical: 4,
                borderRadius: 8,
                width: 'auto',
                height: 'auto',
              }
            ]}>
              <SizableText style={{
                fontSize: 10,
                color: 'white',
                fontWeight: "600",
                textTransform: 'uppercase',
              }}>
                ✓ Completed
              </SizableText>
            </View>

            <SizableText style={{
              fontSize: 12,
              color: getModernTextColor(theme, 'muted'),
              fontWeight: "500",
            }}>
              Document signed and submitted
            </SizableText>
          </XStack>
        </View>

        <Separator
          borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)'}
          style={{ marginVertical: 12 }}
        />

        {/* Person Details */}
        <View style={styles.modernColumn}>
          <SizableText
            style={{
              fontSize: 14,
              color: getModernTextColor(theme, 'primary'),
              fontWeight: "700",
              marginBottom: 8,
            }}
          >
            📝 Person Details
          </SizableText>

          {personDetails.map(([key, value], index) => (
            <View
              key={index + 'person_detail'}
              style={[
                styles.modernInfoRow,
                {
                  backgroundColor: theme ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.08)',
                  marginBottom: 6,
                  borderRadius: 8,
                }
              ]}
            >
              <SizableText
                style={[
                  styles.modernFieldLabel,
                  {
                    color: getModernTextColor(theme, 'muted'),
                    fontSize: 12,
                    fontWeight: '600',
                    textTransform: 'capitalize',
                  }
                ]}
              >
                {parseByDelimiter(key)}:
              </SizableText>
              <SizableText
                numberOfLines={2}
                style={[
                  styles.modernFieldValue,
                  {
                    color: getModernTextColor(theme, 'primary'),
                    fontStyle: value === "No Data" ? 'italic' : 'normal',
                    fontSize: 13,
                    fontWeight: '500',
                  }
                ]}
              >
                {parseIfDate(value)}
              </SizableText>
            </View>
          ))}
        </View>
      </View>
    )
  }

  return useMemo(() => (
    <View style={[
      getModernCardStyle(theme, 'medium'),
      {
        overflow: "hidden",
        minHeight: 360,
        maxWidth: maxWidth || Platform.select({ web: "100%", default: "95%" }),
        borderWidth: 2,
        borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)',
      },
      innerStyle
    ]}
      key={item._id}
      onLayout={({ nativeEvent }) => setDimensions({ width: nativeEvent.layout.width, height: nativeEvent.layout.height })}
    >
      {/* Document Icon/Header Section */}
      <View style={[
        styles.modernImageContainer,
        {
          height: 140,
          position: 'relative',
          overflow: 'hidden',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }
      ]}>
        <Pressable
          style={[
            styles.modernInfoRow,
            {
              flex: 1,
              backgroundColor: theme ? "#f8f9fa" : "#374151",
              position: "relative",
              borderRadius: 0,
              margin: 0,
              padding: 0,
            }
          ]}
          onPress={async () => {
            const res = await handleImage(true);
            await apis.addDataImage(dataId, item._id, res).then(res => {
              item.dataImages.push(res.data.data);
              setKey(key + 1);
            }).finally(() => {
              queryClient.invalidateQueries({ queryKey: ['individualData', dataId] });
            })
          }}
        >
          {item.dataImages && item.dataImages?.length > 0 ? (
            <HorizontalCarousel
              imageStyle={{
                width: "100%",
                height: "100%",
              }}
              images={item.dataImages}
              height={140}
              removeSampleMark
            />
          ) : (
            <View style={[
              styles.modernAnimationContainer,
              {
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: theme ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                maxHeight: 140,
              }
            ]}>
              <LinearGradient
                colors={theme ? ['#667eea', '#764ba2'] : ['#2c3e50', '#34495e']}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
              />

              <View style={[
                {
                  marginBottom: 8,
                  width: 60,
                  height: 60,
                  borderRadius: 20,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  justifyContent: 'center',
                  alignItems: 'center',
                }
              ]}>
                <Feather
                  name="file-text"
                  size={28}
                  color="white"
                />
              </View>
              <SizableText
                size="$3"
                style={{
                  color: 'white',
                  fontWeight: '600',
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}
              >
                Document
              </SizableText>
            </View>
          )}

          {/* Document Type Badge */}
          <View style={[
            getModernActionStyle(theme, 'purple'),
            {
              position: 'absolute',
              top: 12,
              right: 12,
              paddingHorizontal: 12,
              paddingVertical: 8,
              borderRadius: 16,
              width: 'auto',
              height: 'auto',
            }
          ]}>
            <SizableText
              size="$2"
              style={{
                color: 'white',
                fontWeight: 'bold',
                textTransform: 'uppercase',
              }}
            >
              📄 Document
            </SizableText>
          </View>

          {/* Signature Status Badge */}
          <View style={[
            getModernActionStyle(theme, 'green'),
            {
              position: 'absolute',
              bottom: 12,
              left: 12,
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 12,
              width: 'auto',
              height: 'auto',
            }
          ]}>
            <SizableText
              size="$2"
              style={{
                color: 'white',
                fontWeight: 'bold',
              }}
            >
              ✓ Signed
            </SizableText>
          </View>
        </Pressable>
      </View>

      {/* Document Content Section */}
      <View
        style={[
          styles.modernMainContent,
          {
            flex: 1,
            paddingTop: 30,
            minHeight: 180,
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(20px)',
            }),
          }
        ]}
      >
        <DocumentContent />
      </View>
    </View>
  ), [item, key, dimensions]);
}
export default DefaultDocument;